import type { ConcreteComponent } from "vue"

export namespace Kyc {
  export const QuestionnaireTypes = {
    PENSIONS: "PENSIONS",
    INVESTMENTS: "INVESTMENTS",
    // add more types here
  } as const
  export type QuestionnaireTypes = keyof typeof QuestionnaireTypes

  export const UiTypes = {
    Input: { type: "Input", props: { type: "text" } },
    InputDate: { type: "Input", props: { type: "date" } },
    Checkbox: { type: "Checkbox" },
    InputNumber: { type: "InputNumber" },
    PhoneNumber: { type: "PhoneNumber" },
    Search: { type: "Search" },
    PickList: { type: "PickList" },
    PickListVertical: { type: "PickList", props: { direction: "vertical" } },
    Select: { type: "Select" },
    Textarea: { type: "Textarea" },
    RangeSlider: { type: "RangeSlider" },
    Slider: { type: "Slider" },
    Autocomplete: { type: "Autocomplete" },
    Switch: { type: "Switch" },
    Street: { type: "Street" },
    City: { type: "City" },
    Country: { type: "Country" },
    Hidden: { type: "Hidden" },
  } as const
  export type UiTypes = keyof typeof UiTypes

  export interface Navigation {
    chapterId: Nullable<any>
    pageId: Nullable<any>
    questionnaireId: Nullable<any>
    phase: Nullable<number>
  }

  export namespace Navigation {
    export interface ControlCta {
      /** Hiding the CTA. */
      hidden?: boolean
      /** Custom class */
      class?: ClassValue
      /** Custom label */
      label?: MaybeRefOrGetter<string>
      /** Custom validation function, will be called before navigating to the next page. If it returns false, the user cannot continue. */
      onBefore?: () => boolean | Promise<boolean>
      /** Custom callback function, will be called after navigating to the next page. If it returns false, the user cannot continue. */
      onAfter?: () => boolean | Promise<boolean>
    }

    export interface Controls {
      /* Custom isValid ref indicating if the current page is valid. */
      isPageValid?: ComputedRef<boolean>
      next?: ControlCta
      prev?: ControlCta
      manualDispose?: boolean
      getFormState?: () => Record<string, any>
    }
  }

  export namespace Builder {
    type SchemaKey<T> = keyof T | (string & {})
    type FormStructure = Record<string, any>

    export interface UiType {
      type: UiTypes
      props?: Record<string, any>
    }

    export type ValidateFn<Form extends FormStructure = FormStructure, Options = Record<string, any>> = (
      options: Options & {
        answers: Form
        navigateToPage: (page: { chapterId: string; pageId: string; phase?: number }) => void
      }
    ) => boolean | null | undefined

    export interface QuestionProps<Form extends FormStructure = FormStructure> {
      // question: Question<Form>
      errorMessage?: string
    }

    export interface QuestionSchema<
      Form extends FormStructure = FormStructure,
      QuestionKey extends string = string,
    > {
      field: QuestionKey
      class?: ClassValue

      uiType: UiType
      titleKey?: string
      explainMore?: Component
      explainMoreKey?: string
      descriptionKey?: string
      options?: QuestionOption[]
      validate?: ValidateFn<
        Form,
        {
          answer: Form[QuestionKey]
          resetField: VoidFunction
        }
      >
    }

    export type Question<
      Form extends FormStructure = FormStructure,
      QuestionKey extends string = string,
    > = RemoveKeySuffix<QuestionSchema<Form, QuestionKey>>

    export interface QuestionGroup {
      titleKey?: string
      descriptionKey?: string
      explainMoreKey?: string
      visibleIf?: FormStructure
      questions: (QuestionSchema<any, any> | undefined)[]
      class?: ClassValue
    }

    export interface QuestionOption {
      titleKey?: string
      descriptionKey?: string
      value: any
    }

    export interface ComponentProps {
      documentType?: string
      userDetail: string
      group?: string
      hasMultipleDocuments?: boolean
      subGroups?: string[]
    }

    export type PageSchema<Form extends FormStructure = FormStructure, PageKey extends string = string> = {
      id: PageKey
      layout?: {
        hideProgress?: boolean
        hideNavigation?: boolean
        quitWithoutSaving?: boolean
      }
      titleKey?: string
      descriptionKey?: string
      hintKey?: string
      explainMoreKey?: string
      parentField?: SchemaKey<Form>
      questionGroups?: QuestionGroup[]
      component?: (new () => ComponentPublicInstance) | ConcreteComponent | string
      validate?: ValidateFn<Form>
      componentProps?: ComponentProps
    }

    export type Page<Form extends FormStructure = FormStructure> = RemoveKeySuffix<PageSchema<Form>>

    export type Pages<Form extends FormStructure = FormStructure> = {
      [PageKey in keyof Form]: PageSchema<Form, PageKey extends string ? PageKey : string>
    }

    export type Questions<Form extends FormStructure = FormStructure> = {
      [QuestionKey in keyof Form]: QuestionSchema<Form, QuestionKey extends string ? QuestionKey : string>
    }

    /**
     * Customize questionnaire colors using CSS variables:
     * ```css
     * .kyc-theme {
     *   --theme: v-bind("colors.default");
     *   --theme-foreground: v-bind("colors.foreground");
     * }
     * ```
     */
    export interface Theme {
      /** HSL format for default theme. accessible via bg-theme, text-theme, etc. */
      default: string
      /** HSL format for foreground elements. accessible via bg-theme-foreground, text-theme-foreground, etc. */
      foreground: string
    }
  }

  export type PhaseData = {
    [phase: number]: {
      onPhaseDone?: () => Promise<void> | void
      onPhaseStart?: () => Promise<void> | void
      label?: string
    }
  }
}

export type QuestionnaireTypes = keyof typeof Kyc.QuestionnaireTypes

type RemoveKeySuffix<T> = T extends Function
  ? T
  : T extends Array<infer U>
    ? Array<RemoveKeySuffix<U>>
    : T extends object
      ? {
          [K in keyof T as K extends `${infer P}Key` ? P : K]: RemoveKeySuffix<T[K]>
        }
      : T
